/**
 * Premium "Your Travel Plan" quotation PDF.
 *
 *   exportQuotationPDF  — loads destination photos + logo, builds the PDF and downloads it.
 *   buildQuotationPdf   — pure: (data, pre-loaded images) -> jsPDF document.
 *
 * Layout: cover → trip summary → your stays (hotel cards with "Check your hotel" links)
 * → day-by-day itinerary (each day paired with a photo from the Destination model)
 * → inclusions / optional extras / consultant.
 */
import { htmlToPdfText, computeExtraMattress, generatePdfFilename, deriveCarDetailsFromSelections, findMatchingItineraryCard } from './createInquiryCalculation';

const COMPANY = { web: 'www.easotrip.com', phone: '+91 70017 24300', email: 'easogroup@gmail.com', instagram: '@easotrip' };
export const GOOGLE_REVIEWS_URL = 'https://g.page/r/CebrAjG-TJSgEBM/review';
export const INSTAGRAM_URL = 'https://www.instagram.com/easotrip';
// QR images in /public encode the two URLs above — regenerate them if the URLs change
const QR_IMAGES = { review: '/qr-google-review.png', instagram: '/qr-instagram.png' };
// Script font for the consultant's signature (Great Vibes, SIL Open Font License)
const SIGNATURE_FONT_URL = '/fonts/GreatVibes-Regular.ttf';

const C = {
    green: [11, 102, 74],
    greenTint: [241, 246, 243],
    yellow: [246, 196, 38],
    dark: [28, 36, 33],
    body: [55, 65, 61],
    mid: [112, 120, 117],
    rule: [224, 230, 227],
    white: [255, 255, 255],
    red: [190, 40, 40],
};

const MEAL_LABELS = {
    cp_plan: 'Breakfast',
    map_plan: 'Breakfast + 1 major meal',
    ap_plan: 'All meals',
    ep_plan: 'Room only',
};

// Photo boxes (mm) — the loader crops source images to these aspect ratios
export const COVER_PHOTO = { w: 210, h: 104 };
export const DAY_PHOTO = { w: 58, h: 42 };

// ── Text helpers ────────────────────────────────────────────────────────────

// jsPDF's built-in fonts only cover WinAnsi; map common symbols and drop the rest.
const WINANSI_EXTRA = '–—‘’‚“”„•…€™‹›ŒœŠšŸŽžƒ†‡‰';
const clean = (value) => String(value ?? '')
    .replace(/\s*(→|⟶|➝|➔|⇒|->)\s*/g, ' – ')
    .replace(/₹\s?/g, 'Rs. ')
    .replace(/[●○◦▪]/g, '•')
    .replace(/ /g, ' ')
    .split('')
    .filter((ch) => ch.charCodeAt(0) <= 0xff || WINANSI_EXTRA.includes(ch))
    .join('')
    .replace(/[ \t]+/g, ' ')
    .trim();

const asText = (item) => (typeof item === 'string' ? item : (item?.tagValue || item?.tagName || item?.name || ''));

const formatINR = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN')}`;

const joinAnd = (parts) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`);

// ── Date helpers ────────────────────────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseDate = (v) => {
    if (!v) return null;
    const d = new Date(typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v}T00:00:00` : v);
    return isNaN(d) ? null : d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtDayMonth = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
const fmtWeekday = (d) => `${WEEKDAYS[d.getDay()]}, ${fmtDayMonth(d)}`;
const fmtRange = (a, b) => (a.getFullYear() === b.getFullYear()
    ? `${fmtDayMonth(a)} – ${fmtDayMonth(b)} ${b.getFullYear()}`
    : `${fmtDayMonth(a)} ${a.getFullYear()} – ${fmtDayMonth(b)} ${b.getFullYear()}`);

// ── Destination photo matching ──────────────────────────────────────────────

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const buildPlaceIndex = (places = []) => places
    .filter((p) => Array.isArray(p?.images) && p.images.length > 0)
    .flatMap((p) => [p.name, p.label].filter(Boolean).map((n) => ({ key: norm(n), place: p })))
    .filter((k) => k.key.length >= 3);

// Earliest (then longest) destination name mentioned in the text
const findPlace = (text, index) => {
    const hay = ` ${norm(text)} `;
    let best = null;
    index.forEach(({ key, place }) => {
        const pos = hay.indexOf(` ${key} `);
        if (pos < 0) return;
        if (!best || pos < best.pos || (pos === best.pos && key.length > best.len)) best = { pos, len: key.length, place };
    });
    return best?.place || null;
};

/**
 * Decide which Destination photo goes with the cover and with each day.
 * A day is matched by its route title (arrival point first, e.g. "Gangtok → Lachung" → Lachung),
 * then by that night's hotel location, then by the trip destination.
 * Repeated destinations rotate through their images so days don't show the same photo.
 */
export const planQuotationPhotos = ({ places, tripTitle, days }) => {
    const index = buildPlaceIndex(places);
    const used = new Map();
    const take = (place, freshOnly = false) => {
        if (!place) return null;
        const id = String(place._id || place.name);
        const n = used.get(id) || 0;
        if (freshOnly && n >= place.images.length) return null;
        used.set(id, n + 1);
        return { url: place.images[n % place.images.length], caption: clean(place.name || place.label) };
    };

    const tripPlace = findPlace(tripTitle, index);
    const cover = take(tripPlace || findPlace(days.map((d) => d.title).join(' '), index));

    const dayPhotos = days.map((day) => {
        const segments = String(day.rawTitle || '').split(/\s*(?:→|⟶|➝|➔|⇒|->|\bto\b|\s[-–—]\s)\s*/i).filter(Boolean);
        const candidates = [...segments.reverse(), day.overnightRaw, day.hotelDestination];
        for (const text of candidates) {
            const place = text && findPlace(text, index);
            if (place) return take(place);
        }
        // Trip-level photo only if it hasn't been shown yet
        return take(tripPlace, true);
    });

    return { cover, days: dayPhotos };
};

// ── Data shaping ────────────────────────────────────────────────────────────

const getNightRows = (hotelSelections = {}, nights) => Object.keys(hotelSelections || {})
    .sort((a, b) => Number(a) - Number(b))
    .slice(0, nights)
    .map((k) => {
        const v = hotelSelections[k];
        return (Array.isArray(v) ? v : (v ? [v] : [])).filter((s) => s && s.hotelId);
    });

const findHotel = (allHotels, id) => (allHotels || []).find((h) => String(h._id) === String(id));

export const hotelLink = (hotel) => {
    if (hotel?.hotel_link && /^https?:\/\//i.test(hotel.hotel_link.trim())) return hotel.hotel_link.trim();
    const q = [hotel?.hotel_name, hotel?.sub_destination, hotel?.destination]
        .map((s) => (s || '').trim())
        .filter((s, i, arr) => s && arr.findIndex((x) => x.toLowerCase() === s.toLowerCase()) === i)
        .join(' ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

// Split a detailed-itinerary HTML blob into per-day line arrays using its "Day N" headings
const splitBlobByDay = (html) => {
    const byDay = [];
    let current = null;
    htmlToPdfText(html).split(/\r?\n/).forEach((line) => {
        const m = line.trim().match(/^Day\s*0?(\d+)\b/i);
        if (m) { current = Number(m[1]) - 1; byDay[current] = byDay[current] || []; return; }
        if (current !== null && line.trim()) byDay[current].push(line);
    });
    return byDay;
};

const toBodyLines = (rawLines) => {
    const lines = [];
    let overnight = null;
    rawLines.forEach((raw) => {
        const trimmed = raw.trim();
        if (!trimmed) return;
        const night = trimmed.match(/^Overnight(?:\s+Stay)?\s*(?:at|in)?\s*[:\-–]?\s*(.+)$/i);
        if (night) { overnight = clean(night[1]); return; }
        const indent = (raw.match(/^(\s*)/)[1] || '').length;
        const list = trimmed.match(/^([•○●▪\-*]|\d+\.|[a-z]\.)\s+(.*)$/i);
        const text = clean(list ? list[2] : trimmed);
        if (!text) return;
        const isNote = /^(⚠|note\s*:|optional\s*:)/i.test(trimmed);
        lines.push({ text, depth: indent >= 2 ? 1 : 0, isNote });
    });
    return { lines, overnight };
};

export const shapeQuotationData = (data) => {
    const {
        guestInfo = {}, tripDetails = {}, stayInfo = {}, itinerary = [], hotelSelections = {}, allHotels = [],
        selectedPackage, cost = 0, detailedItinerary, dayWiseDetailedItinerary = [], carSelections = {},
        inclusions = [], exclusions = [], storedItineraries = [], allPackages = [],
    } = data;

    const nights = parseInt(tripDetails.duration, 10) || 0;
    const start = parseDate(tripDetails.travel_date);
    const nightRows = getNightRows(hotelSelections, nights);

    const blobDays = (!dayWiseDetailedItinerary?.some((d) => d?.para?.length) && typeof detailedItinerary === 'string' && detailedItinerary.trim())
        ? splitBlobByDay(detailedItinerary) : [];

    const dayCount = Math.max(itinerary?.length || 0, dayWiseDetailedItinerary?.length || 0, blobDays.length);
    const totalDays = dayCount || (nights + 1);

    const activeCars = (tripDetails.car_details || []).filter((c) => Number(c.car_count) > 0);
    const dayCars = deriveCarDetailsFromSelections(carSelections, totalDays);
    const hasAnyDayCars = dayCars.length > 0;
    const days = Array.from({ length: dayCount }, (_, i) => {
        const wise = dayWiseDetailedItinerary?.[i];
        const rawTitle = asText(itinerary?.[i]) || wise?.dayTitle || `Day ${i + 1}`;
        let rawLines = wise?.para?.length
            ? wise.para.flatMap((p) => htmlToPdfText(p).split(/\r?\n/))
            : (blobDays[i] || []);

        if (!rawLines.length && rawTitle) {
            const matched = findMatchingItineraryCard(rawTitle, storedItineraries, allPackages);
            if (matched && matched.description) {
                rawLines = htmlToPdfText(matched.description).split(/\r?\n/);
            }
        }
        const { lines, overnight } = toBodyLines(rawLines);
        const hotel = nightRows[i]?.[0] ? findHotel(allHotels, nightRows[i][0].hotelId) : null;
        const overnightRaw = i < nights ? (overnight || hotel?.sub_destination || hotel?.destination || '') : '';
        const dayCarsForThisDay = (carSelections?.[i] || []).filter((c) => c && Number(c.car_count) > 0);
        const cars = hasAnyDayCars ? dayCarsForThisDay : activeCars;
        return {
            title: clean(rawTitle),
            rawTitle,
            date: start ? addDays(start, i) : null,
            lines,
            overnightRaw,
            overnight: clean(overnightRaw),
            hotelDestination: hotel ? `${hotel.sub_destination || ''} ${hotel.destination || ''}` : '',
            meta: clean(cars.map((c) => `${c.car_name}${Number(c.car_count) > 1 ? ` × ${c.car_count}` : ''}`).join(' · ')),
        };
    });

    // Group nights by hotel → stay cards
    const stays = [];
    const stayByHotel = new Map();
    nightRows.forEach((rows, n) => {
        const perHotel = new Map();
        rows.forEach((sel) => {
            const hotel = findHotel(allHotels, sel.hotelId);
            if (!hotel) return;
            const entry = perHotel.get(hotel._id) || { hotel, rooms: new Map(), meals: new Set() };
            const roomType = clean(sel.roomType) || 'Room';
            entry.rooms.set(roomType, (entry.rooms.get(roomType) || 0) + (parseInt(sel.roomsCount, 10) || parseInt(stayInfo.rooms, 10) || 1));
            entry.meals.add(MEAL_LABELS[sel.mealPlan] || clean(sel.mealPlan) || '—');
            perHotel.set(hotel._id, entry);
        });
        perHotel.forEach((entry, id) => {
            let stay = stayByHotel.get(id);
            if (!stay) {
                stay = { hotel: entry.hotel, nights: [], rooms: new Map(), meals: new Set() };
                stayByHotel.set(id, stay);
                stays.push(stay);
            }
            stay.nights.push(n);
            entry.rooms.forEach((count, rt) => stay.rooms.set(rt, Math.max(stay.rooms.get(rt) || 0, count)));
            entry.meals.forEach((m) => stay.meals.add(m));
        });
    });

    // Vehicles summary (day-wise selections take precedence, falling back to legacy trip-level car_details)
    const chosenCars = hasAnyDayCars ? dayCars : activeCars;
    const vehicles = clean(chosenCars.map((c) => `${c.car_name} × ${c.car_count}`).join(' · '));

    const pax = parseInt(tripDetails.pax, 10) || 0;
    const kids = parseInt(tripDetails.kids_above_5, 10) || 0;
    const mattress = computeExtraMattress(hotelSelections, tripDetails);
    let travellers = `${pax} Adult${pax === 1 ? '' : 's'}`;
    if (kids) travellers += `, ${kids} Kid${kids === 1 ? '' : 's'} (5+)`;
    if (mattress.hasAny) travellers += ` (${mattress.uniform ? '' : 'up to '}${mattress.max} extra mattress per night)`;

    const pkgInclusions = selectedPackage?.details?.cost?.inclusions || [];
    const pkgExclusions = selectedPackage?.details?.cost?.exclusions || [];

    const routePlaces = [];
    days.forEach((d) => { if (d.overnight && !routePlaces.includes(d.overnight)) routePlaces.push(d.overnight); });

    const tripTitle = clean(tripDetails.location || selectedPackage?.location || selectedPackage?.label) || 'Your Holiday';
    const rooms = parseInt(stayInfo.rooms, 10) || 0;

    return {
        tripTitle,
        guestName: clean(guestInfo.guest_name) || 'Guest',
        guestContact: clean([
            guestInfo.guest_phone ? `${guestInfo.country_code || '+91'} ${guestInfo.guest_phone}` : '',
            guestInfo.guest_email,
        ].filter(Boolean).join(' · ')),
        nights,
        start,
        end: start ? addDays(start, Math.max(nights, dayCount - 1, 0)) : null,
        travellers,
        pax,
        rooms,
        vehicles,
        pickup: clean(tripDetails.pickup_location),
        drop: clean(tripDetails.dropoff_location),
        cost: Number(cost) || 0,
        days,
        stays,
        routePlaces,
        inclusions: (inclusions.length ? inclusions : pkgInclusions).map((x) => clean(asText(x))).filter(Boolean),
        exclusions: (exclusions.length ? exclusions : pkgExclusions).map((x) => clean(asText(x))).filter(Boolean),
        extras: (tripDetails.optional_extras || []).map((e) => ({
            name: clean(e.name),
            detail: clean(e.detail || ''),
            count: Number(e.count) || 1,
            price: (Number(e.price) || 0) * (Number(e.count) || 1),
            included: e.include_in_cost !== false,
        })).filter((e) => e.name),
        notes: String(tripDetails.important_notes || '').split(/\r?\n/).map(clean).filter(Boolean),
    };
};

// ── PDF builder ─────────────────────────────────────────────────────────────

const imageFormat = (dataUrl) => (/^data:image\/png/i.test(dataUrl) ? 'PNG' : 'JPEG');

export const buildQuotationPdf = async (data, assets = {}) => {
    const jspdfModule = await import('jspdf');
    const jsPDF = jspdfModule.jsPDF || jspdfModule.default;
    const q = shapeQuotationData(data);
    const { logo, cover, dayPhotos = [], consultant = {}, quotationNo, qr = {}, signatureFont } = assets;

    const pdf = new jsPDF('p', 'mm', 'a4');
    let hasSignatureFont = false;
    if (signatureFont) {
        try {
            pdf.addFileToVFS('GreatVibes-Regular.ttf', signatureFont);
            pdf.addFont('GreatVibes-Regular.ttf', 'GreatVibes', 'normal');
            hasSignatureFont = true;
        } catch (_) { /* fall back to Times italic */ }
    }
    const W = pdf.internal.pageSize.getWidth();
    const H = pdf.internal.pageSize.getHeight();
    const M = 16;
    const CW = W - M * 2;
    const TOP = 18;
    const BOTTOM = H - 20;
    let y = TOP;

    // ── primitives
    const font = (family, style, size, color) => {
        pdf.setFont(family, style);
        pdf.setFontSize(size);
        if (color) pdf.setTextColor(...color);
    };
    const fill = (c) => pdf.setFillColor(...c);
    const stroke = (c, w = 0.3) => { pdf.setDrawColor(...c); pdf.setLineWidth(w); };
    const hair = (x1, yy, x2, color = C.rule) => { stroke(color, 0.25); pdf.line(x1, yy, x2, yy); };
    const wrap = (text, width) => pdf.splitTextToSize(text, width);
    const ensure = (need) => {
        if (y + need > BOTTOM) { pdf.addPage(); y = TOP; return true; }
        return false;
    };
    // Letter-spaced uppercase label
    const kicker = (text, x, yy, color = C.green, size = 6.5, opts = {}) => {
        font('helvetica', 'bold', size, color);
        pdf.text(text.toUpperCase(), x, yy, { charSpace: 0.9, ...opts });
    };
    const drawPhoto = (dataUrl, x, yy, w, h, r = 2.5) => {
        pdf.saveGraphicsState();
        pdf.roundedRect(x, yy, w, h, r, r, null);
        pdf.clip();
        pdf.discardPath();
        pdf.addImage(dataUrl, imageFormat(dataUrl), x, yy, w, h, undefined, 'FAST');
        pdf.restoreGraphicsState();
    };
    const arrowUpRight = (x, yy, s = 2.2, color = C.green) => {
        stroke(color, 0.4);
        pdf.line(x, yy, x + s, yy - s);
        pdf.line(x + s * 0.35, yy - s, x + s, yy - s);
        pdf.line(x + s, yy - s, x + s, yy - s * 0.35);
    };
    const tick = (x, yy, color = C.green) => {
        stroke(color, 0.45);
        pdf.line(x, yy - 1.1, x + 1.05, yy);
        pdf.line(x + 1.05, yy, x + 2.9, yy - 2.4);
    };
    const cross = (x, yy, color = C.red) => {
        stroke(color, 0.45);
        pdf.line(x + 0.2, yy - 2.2, x + 2.2, yy - 0.2);
        pdf.line(x + 2.2, yy - 2.2, x + 0.2, yy - 0.2);
    };
    const star = (cx, cy, r, color = C.yellow) => {
        const pts = [];
        for (let i = 0; i < 10; i++) {
            const a = -Math.PI / 2 + (i * Math.PI) / 5;
            const rr = i % 2 === 0 ? r : r * 0.45;
            pts.push([cx + rr * Math.cos(a), cy + rr * Math.sin(a)]);
        }
        const deltas = pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]);
        fill(color);
        pdf.lines(deltas, pts[0][0], pts[0][1], [1, 1], 'F', true);
    };
    // Small Instagram glyph: rounded square + lens + dot
    const instagramIcon = (x, yy, s, color) => {
        stroke(color, s * 0.11);
        pdf.roundedRect(x, yy, s, s, s * 0.3, s * 0.3, 'S');
        pdf.circle(x + s / 2, yy + s / 2, s * 0.24, 'S');
        fill(color);
        pdf.circle(x + s * 0.77, yy + s * 0.23, s * 0.07, 'F');
    };
    const sectionHeader = (title, sub) => {
        ensure(26);
        font('times', 'bold', 21, C.dark);
        pdf.text(title, M, y + 7);
        if (sub) {
            const tw = pdf.getTextWidth(title);
            kicker(sub, M + tw + 4, y + 6.6, C.mid, 6.3);
        }
        stroke(C.green, 0.5);
        pdf.line(M, y + 11, M + CW, y + 11);
        y += 18;
    };

    // ════════════════════════════════════════════════════════════════════
    // PAGE 1 — COVER
    // ════════════════════════════════════════════════════════════════════
    if (logo) pdf.addImage(logo, imageFormat(logo), M, 9, 42, 42 / 4.85);
    else { font('helvetica', 'bold', 18, C.green); pdf.text('EASOTRIP', M, 17); }

    const coverY = 26;
    if (cover?.data) {
        pdf.addImage(cover.data, imageFormat(cover.data), 0, coverY, COVER_PHOTO.w, COVER_PHOTO.h, undefined, 'FAST');
        if (cover.caption) {
            font('helvetica', 'normal', 7);
            const capW = pdf.getTextWidth(cover.caption) + 6;
            fill(C.white);
            pdf.roundedRect(W - M - capW, coverY + COVER_PHOTO.h - 9, capW, 5.6, 2.8, 2.8, 'F');
            pdf.setTextColor(...C.green);
            pdf.text(cover.caption, W - M - capW / 2, coverY + COVER_PHOTO.h - 5.2, { align: 'center' });
        }
    } else {
        // Illustrated fallback: layered mountain silhouettes
        fill(C.greenTint);
        pdf.rect(0, coverY, W, COVER_PHOTO.h, 'F');
        const base = coverY + COVER_PHOTO.h;
        fill([214, 230, 222]);
        pdf.triangle(-20, base, 60, coverY + 30, 140, base, 'F');
        pdf.triangle(80, base, 160, coverY + 22, 240, base, 'F');
        fill([182, 211, 197]);
        pdf.triangle(20, base, 95, coverY + 48, 170, base, 'F');
        fill(C.white);
        pdf.triangle(160, coverY + 22, 152, coverY + 30, 168, coverY + 30, 'F');
        pdf.triangle(60, coverY + 30, 53, coverY + 37, 67, coverY + 37, 'F');
    }

    y = coverY + COVER_PHOTO.h + 14;
    kicker('Your Travel Plan', M, y, C.green, 7);
    y += 11;
    font('times', 'bold', 30, C.dark);
    const titleLines = wrap(q.tripTitle, CW);
    pdf.text(titleLines, M, y);
    y += (titleLines.length - 1) * 11 + 7;

    const durationStr = q.nights ? `${q.nights} Night${q.nights === 1 ? '' : 's'} / ${q.nights + 1} Days` : '';
    const subtitle = [q.routePlaces.join(' · '), durationStr].filter(Boolean).join('   |   ');
    if (subtitle) {
        font('helvetica', 'normal', 9.5, C.mid);
        const subLines = wrap(subtitle, CW);
        pdf.text(subLines, M, y);
        y += subLines.length * 4.6;
    }
    fill(C.yellow);
    pdf.rect(M, y + 1.5, 18, 1.6, 'F');
    y += 12;

    font('helvetica', 'normal', 9.5, C.body);
    pdf.text('Dear ', M, y);
    const dearW = pdf.getTextWidth('Dear ');
    font('helvetica', 'bold', 9.5, C.dark);
    pdf.text(`${q.guestName},`, M + dearW, y);
    y += 5;
    font('helvetica', 'normal', 9.5, C.body);
    const intro = `Thank you for choosing EasoTrip. Here is the plan we've put together for your ${q.tripTitle} trip — every stay, every day and every inclusion in one place. Take a look, and let your consultant know if you'd like anything changed.`;
    const introLines = wrap(intro, CW);
    pdf.text(introLines, M, y, { lineHeightFactor: 1.45 });
    y += introLines.length * 4.9 + 6;

    // Info strip: dates · travellers · pickup/drop
    const pickDrop = q.pickup && q.drop && q.pickup !== q.drop ? `${q.pickup} / ${q.drop}` : (q.pickup || q.drop || '—');
    const infoCols = [
        ['Dates', q.start && q.end ? fmtRange(q.start, q.end) : '—'],
        ['Travellers', q.travellers.replace(/\s*\(.*\)$/, '')],
        ['Pickup / Drop', pickDrop],
    ];
    const colW = CW / 3;
    const infoH = 17;
    stroke(C.rule, 0.3);
    fill(C.white);
    pdf.roundedRect(M, y, CW, infoH, 2.5, 2.5, 'FD');
    infoCols.forEach(([label, value], i) => {
        const x = M + colW * i;
        if (i > 0) { stroke(C.rule, 0.25); pdf.line(x, y + 3, x, y + infoH - 3); }
        kicker(label, x + 5, y + 6, C.mid, 5.8);
        font('helvetica', 'bold', 9.5, C.dark);
        pdf.text(wrap(value, colW - 9)[0], x + 5, y + 12);
    });
    y += infoH + 8;

    {
        // Quotation ID card on Cover
        const boxH = 22;
        stroke(C.green, 0.5);
        fill(C.greenTint);
        pdf.roundedRect(M, y, CW, boxH, 2.5, 2.5, 'FD');
        kicker('Quotation ID', M + 6, y + 6.5, C.green, 6.3);
        font('helvetica', 'bold', 16, quotationNo ? C.dark : C.mid);
        pdf.text(quotationNo || 'DRAFT', M + 6, y + 14, { charSpace: 0.5 });
        font('helvetica', 'normal', 7.5, C.mid);
        pdf.text(quotationNo
            ? 'Please mention this ID whenever you contact us about this trip.'
            : 'Your quotation ID will be assigned once this plan is saved.', M + 6, y + 19);
    }

    // Cover footer bar
    fill(C.green);
    pdf.rect(0, H - 13, W, 13, 'F');
    font('helvetica', 'normal', 8, C.white);
    pdf.text(COMPANY.web, M, H - 5.2);
    pdf.link(M, H - 9, pdf.getTextWidth(COMPANY.web), 6, { url: `https://${COMPANY.web}` });
    pdf.text(COMPANY.phone, M + CW * 0.36, H - 5.2, { align: 'center' });
    pdf.text(COMPANY.email, M + CW * 0.66, H - 5.2, { align: 'center' });
    const igW = pdf.getTextWidth(COMPANY.instagram);
    pdf.text(COMPANY.instagram, W - M, H - 5.2, { align: 'right' });
    instagramIcon(W - M - igW - 4.6, H - 8.1, 3.2, C.white);
    pdf.link(W - M - igW - 5, H - 9, igW + 5, 6, { url: INSTAGRAM_URL });

    // ════════════════════════════════════════════════════════════════════
    // PAGE 2 — TRIP SUMMARY
    // ════════════════════════════════════════════════════════════════════
    pdf.addPage();
    y = TOP;
    sectionHeader('Trip Summary', 'Everything at a glance');

    const tableW = CW * 0.6;
    const labelW = 30;
    const priceX = M + tableW + 7;
    const priceW = CW - tableW - 7;

    // Price card
    {
        const sub = `For ${q.pax} adult${q.pax === 1 ? '' : 's'}${q.rooms ? ` · ${q.rooms} room${q.rooms === 1 ? '' : 's'}` : ''} · as per inclusions`;
        font('helvetica', 'normal', 7.3);
        const subLines = wrap(sub, priceW - 12);
        const cardH = 26 + subLines.length * 3.6;
        fill(C.green);
        pdf.roundedRect(priceX, y, priceW, cardH, 3, 3, 'F');
        kicker('Total Package Price', priceX + 6, y + 8, C.white, 5.8);
        font('helvetica', 'bold', 11, C.yellow);
        pdf.text('Rs.', priceX + 6, y + 18);
        const rsW = pdf.getTextWidth('Rs. ');
        font('helvetica', 'bold', 19, C.white);
        pdf.text(Number(q.cost).toLocaleString('en-IN'), priceX + 6 + rsW, y + 18);
        font('helvetica', 'normal', 7.3, [214, 236, 226]);
        pdf.text(subLines, priceX + 6, y + 23.5);
    }

    const summaryRows = [
        ['Guest', q.guestName, q.guestContact],
        ['Travel dates', q.start && q.end ? `${fmtWeekday(q.start)} – ${fmtWeekday(q.end)} ${q.end.getFullYear()}` : '—'],
        ['Duration', durationStr || '—'],
        ['Travellers', q.travellers],
        ['Rooms', q.rooms ? `${q.rooms} Room${q.rooms === 1 ? '' : 's'}` : '—'],
        ['Vehicles', q.vehicles || '—'],
        ['Pickup', q.pickup || '—'],
        ['Drop', q.drop || '—'],
    ];
    summaryRows.forEach(([label, value, second]) => {
        font('helvetica', 'normal', 9.3);
        const vLines = wrap(value, tableW - labelW - 2);
        font('helvetica', 'normal', 7.5);
        const sLines = second ? wrap(second, tableW - labelW - 2) : [];
        const rowH = 5.2 + vLines.length * 4.4 + sLines.length * 3.7 + 1.5;
        font('helvetica', 'normal', 8.2, C.mid);
        pdf.text(label, M, y + 5.6);
        font('helvetica', 'normal', 9.3, C.dark);
        pdf.text(vLines, M + labelW, y + 5.6, { lineHeightFactor: 1.35 });
        if (sLines.length) {
            font('helvetica', 'normal', 7.5, C.mid);
            pdf.text(sLines, M + labelW, y + 5.6 + vLines.length * 4.4);
        }
        y += rowH;
        hair(M, y, M + tableW);
    });

    // Route at a glance
    if (q.days.length) {
        y += 10;
        kicker('Route at a glance', M, y, C.mid, 6.3);
        y += 4;
        const dateX = M + 18;
        const titleX = M + 42;
        const titleW = CW - 42 - 34;
        q.days.forEach((d, i) => {
            font('helvetica', 'normal', 8.6);
            const lines = wrap(d.title, titleW);
            const rowH = 4 + lines.length * 4.1 + 1.6;
            ensure(rowH);
            if (i % 2 === 0) { fill(C.greenTint); pdf.rect(M, y, CW, rowH, 'F'); }
            font('helvetica', 'bold', 8.6, C.green);
            pdf.text(`Day ${i + 1}`, M + 3, y + 5.2);
            font('helvetica', 'normal', 8, C.mid);
            if (d.date) pdf.text(fmtWeekday(d.date).replace(',', ''), dateX, y + 5.2);
            font('helvetica', 'normal', 8.6, C.dark);
            pdf.text(lines, titleX, y + 5.2, { lineHeightFactor: 1.3 });
            font('helvetica', 'normal', 8, C.mid);
            pdf.text(d.overnight || '—', M + CW - 3, y + 5.2, { align: 'right' });
            y += rowH;
            hair(M, y, M + CW, [236, 240, 238]);
        });
    }

    // ════════════════════════════════════════════════════════════════════
    // YOUR STAYS
    // ════════════════════════════════════════════════════════════════════
    if (q.stays.length) {
        pdf.addPage();
        y = TOP;
        const totalNights = new Set(q.stays.flatMap((s) => s.nights)).size;
        sectionHeader('Your Stays', `${q.stays.length} hotel${q.stays.length === 1 ? '' : 's'} · ${totalNights} night${totalNights === 1 ? '' : 's'}`);

        const gap = 6;
        const cardW = (CW - gap) / 2;
        const pad = 5.5;
        const innerW = cardW - pad * 2;
        const valX = 20;

        const measure = (s) => {
            const name = clean(s.hotel.hotel_name) || 'Hotel';
            const nightNums = s.nights.map((n) => n + 1);
            const nightLabel = `${nightNums.length > 1 ? 'Nights' : 'Night'} ${joinAnd(nightNums.map(String))}`;
            const nightDates = q.start ? s.nights.map((n) => fmtDayMonth(addDays(q.start, n))).join(' · ') : '';
            const rooms = [...s.rooms].map(([rt, c]) => `${rt} × ${c}`).join(', ');
            const meals = [...s.meals].join(' / ');
            font('times', 'bold', 13.5);
            const nameLines = wrap(name, innerW);
            font('helvetica', 'normal', 8);
            const nightLines = wrap(`${nightLabel}${nightDates ? `  ·  ${nightDates}` : ''}`, innerW);
            font('helvetica', 'normal', 8.2);
            const roomLines = wrap(rooms || '—', innerW - valX);
            const mealLines = wrap(meals || '—', innerW - valX);
            const h = pad + 4 + nameLines.length * 5.6 + nightLines.length * 3.9 + 3
                + (roomLines.length * 3.9 + 4.4) + (mealLines.length * 3.9 + 4.4) + 4 + 7.5 + pad;
            return { name, nightLabel, nightDates, nameLines, roomLines, mealLines, h, city: clean(s.hotel.sub_destination || s.hotel.destination) };
        };

        const drawStay = (s, m, x, top, h) => {
            stroke(C.rule, 0.3);
            fill(C.white);
            pdf.roundedRect(x, top, cardW, h, 2.5, 2.5, 'FD');
            stroke(C.green, 1.1);
            pdf.line(x + 2.2, top + 0.35, x + cardW - 2.2, top + 0.35);

            let cy = top + pad + 2.2;
            if (m.city) kicker(m.city, x + pad, cy, C.green, 6);
            cy += 5.6;
            font('times', 'bold', 13.5, C.dark);
            pdf.text(m.nameLines, x + pad, cy, { lineHeightFactor: 1.15 });
            cy += (m.nameLines.length - 1) * 5.6 + 4.6;
            font('helvetica', 'bold', 8, C.dark);
            pdf.text(m.nightLabel, x + pad, cy);
            if (m.nightDates) {
                const lw = pdf.getTextWidth(m.nightLabel);
                font('helvetica', 'normal', 8, C.mid);
                const rest = wrap(`  ·  ${m.nightDates}`, innerW - lw);
                pdf.text(rest[0], x + pad + lw, cy);
                if (rest.length > 1) { cy += 3.9; pdf.text(wrap(rest.slice(1).join(' '), innerW), x + pad, cy); }
            }
            cy += 3;
            [['Room', m.roomLines], ['Meals', m.mealLines]].forEach(([label, lines]) => {
                hair(x + pad, cy, x + cardW - pad);
                cy += 4.4;
                font('helvetica', 'normal', 8, C.mid);
                pdf.text(label, x + pad, cy);
                font('helvetica', 'normal', 8.2, C.dark);
                pdf.text(lines, x + pad + valX, cy, { lineHeightFactor: 1.3 });
                cy += lines.length * 3.9;
            });
            hair(x + pad, cy, x + cardW - pad);

            // "Check your hotel ↗" pill — links to the hotel's Google profile
            const btnY = top + h - pad - 7.5;
            font('helvetica', 'bold', 7.8, C.green);
            const label = 'Check your hotel';
            const btnW = pdf.getTextWidth(label) + 14;
            stroke(C.green, 0.4);
            pdf.roundedRect(x + pad, btnY, btnW, 7.5, 3.75, 3.75, 'S');
            pdf.text(label, x + pad + 4.5, btnY + 4.95);
            arrowUpRight(x + pad + 4.5 + pdf.getTextWidth(label) + 2.3, btnY + 5.1, 2);
            pdf.link(x + pad, btnY, btnW, 7.5, { url: hotelLink(s.hotel) });
        };

        for (let i = 0; i < q.stays.length; i += 2) {
            const pair = q.stays.slice(i, i + 2).map((s) => ({ s, m: measure(s) }));
            const rowH = Math.max(...pair.map((p) => p.m.h));
            ensure(rowH + 2);
            const rowTop = y;
            pair.forEach(({ s, m }, j) => drawStay(s, m, M + j * (cardW + gap), rowTop, rowH));
            y += rowH + gap;
        }
        font('helvetica', 'italic', 7.5, C.mid);
        pdf.text('Tap “Check your hotel” to see real guest photos, reviews and location.', M, y + 1);
        y += 12;
    }

    // ════════════════════════════════════════════════════════════════════
    // DETAILED ITINERARY
    // ════════════════════════════════════════════════════════════════════
    if (q.days.length) {
        if (!q.stays.length) { pdf.addPage(); y = TOP; }
        sectionHeader('Detailed Itinerary', 'Day-by-day plan');

        const badgeW = 13;
        const textX = M + badgeW + 6;
        const lineH = 4.3;

        q.days.forEach((d, i) => {
            const photo = dayPhotos[i];
            const hasPhoto = Boolean(photo?.data);
            const textW = CW - (badgeW + 6) - (hasPhoto ? DAY_PHOTO.w + 7 : 0);

            // measure
            font('times', 'bold', 13.5);
            const titleLines = wrap(d.title, textW);
            font('helvetica', 'normal', 8.8);
            const body = d.lines.map((l) => ({ ...l, wrapped: wrap(l.text, textW - (l.depth ? 9 : 4.5)) }));
            const headH = 4 + titleLines.length * 5.6 + (d.meta ? 4.4 : 0) + 3;
            const bodyH = body.reduce((h, l) => h + l.wrapped.length * lineH + 1.3, 0);
            const textH = headH + bodyH + (d.overnight ? 7 : 0);
            const blockH = Math.max(textH, hasPhoto ? DAY_PHOTO.h + 7 : 16);
            ensure(Math.min(blockH, 70) + 4);

            const top = y;
            const topPage = pdf.internal.getNumberOfPages();
            // Day badge
            fill(C.green);
            pdf.roundedRect(M, top, badgeW, 15, 2, 2, 'F');
            font('helvetica', 'bold', 5.2, [200, 230, 215]);
            pdf.text('DAY', M + badgeW / 2, top + 5, { align: 'center', charSpace: 0.6 });
            font('helvetica', 'bold', 13, C.white);
            pdf.text(String(i + 1).padStart(2, '0'), M + badgeW / 2, top + 11.8, { align: 'center' });

            // Photo (right column)
            if (hasPhoto) {
                const px = M + CW - DAY_PHOTO.w;
                drawPhoto(photo.data, px, top, DAY_PHOTO.w, DAY_PHOTO.h);
                if (photo.caption) {
                    font('helvetica', 'normal', 7.2, C.green);
                    pdf.text(wrap(photo.caption, DAY_PHOTO.w)[0], px + DAY_PHOTO.w / 2, top + DAY_PHOTO.h + 4.2, { align: 'center' });
                }
            }

            // Header text
            let ty = top + 3.2;
            font('helvetica', 'normal', 7.6, C.mid);
            if (d.date) pdf.text(fmtWeekday(d.date), textX, ty);
            ty += 5.6;
            font('times', 'bold', 13.5, C.dark);
            pdf.text(titleLines, textX, ty, { lineHeightFactor: 1.15 });
            ty += (titleLines.length - 1) * 5.6;
            if (d.meta) {
                ty += 4.4;
                font('helvetica', 'bold', 7.6, C.green);
                pdf.text(d.meta, textX, ty);
            }
            y = ty + 5.5;

            // Body bullets (may continue onto the next page)
            body.forEach((l) => {
                const h = l.wrapped.length * lineH + 1.3;
                if (ensure(h)) y += 2;
                const bx = textX + (l.depth ? 4.5 : 0);
                const tx = bx + 4.5;
                font('helvetica', l.isNote ? 'italic' : 'normal', 8.8, l.isNote ? C.mid : C.body);
                pdf.text(l.wrapped, tx, y, { lineHeightFactor: 1.38 });
                if (l.depth) { font('helvetica', 'normal', 8.8, C.mid); pdf.text('–', bx, y); }
                else { fill(C.green); pdf.circle(bx + 0.9, y - 1.1, 0.65, 'F'); }
                y += h;
            });

            if (d.overnight) {
                ensure(7);
                y += 2;
                font('helvetica', 'normal', 8, C.mid);
                pdf.text('Overnight: ', textX, y);
                const ow = pdf.getTextWidth('Overnight: ');
                font('helvetica', 'bold', 8, C.green);
                pdf.text(d.overnight, textX + ow, y);
                y += 3;
            }

            // Leave room for the photo if this day stayed on the same page
            if (hasPhoto && pdf.internal.getNumberOfPages() === topPage) {
                y = Math.max(y, top + DAY_PHOTO.h + 6);
            }
            y += 5;
            if (i < q.days.length - 1) {
                hair(M, y, M + CW);
                y += 7;
            }
        });
        y += 6;
    }

    // ════════════════════════════════════════════════════════════════════
    // WHAT'S INCLUDED
    // ════════════════════════════════════════════════════════════════════
    if (q.inclusions.length || q.exclusions.length) {
        if (y + 90 > BOTTOM) { pdf.addPage(); y = TOP; }
        sectionHeader("What's Included", 'Inclusions & exclusions');

        const both = q.inclusions.length && q.exclusions.length;
        const gap = 6;
        const cardW = both ? (CW - gap) / 2 : CW;
        const pad = 6;
        const lists = [
            { title: 'Included', items: q.inclusions, color: C.green, icon: tick },
            { title: 'Not included', items: q.exclusions, color: C.red, icon: cross },
        ].filter((l) => l.items.length);

        font('helvetica', 'normal', 8.6);
        lists.forEach((l) => {
            l.wrapped = l.items.map((t) => wrap(t, cardW - pad * 2 - 6));
            l.h = pad + 7 + l.wrapped.reduce((h, w) => h + w.length * 4.1 + 1.9, 0) + pad - 2;
        });
        const cardH = Math.max(...lists.map((l) => l.h));
        ensure(cardH);
        lists.forEach((l, j) => {
            const x = M + j * (cardW + gap);
            stroke(C.rule, 0.3);
            fill(C.white);
            pdf.roundedRect(x, y, cardW, cardH, 2.5, 2.5, 'FD');
            font('helvetica', 'bold', 9.5, l.color);
            pdf.text(l.title, x + pad, y + pad + 3);
            let ly = y + pad + 10;
            l.wrapped.forEach((w) => {
                l.icon(x + pad, ly);
                font('helvetica', 'normal', 8.6, C.body);
                pdf.text(w, x + pad + 6, ly, { lineHeightFactor: 1.35 });
                ly += w.length * 4.1 + 1.9;
            });
        });
        y += cardH + 12;
    }

    // ── Optional extras
    if (q.extras.length) {
        const payable = q.extras.filter((e) => !e.included).length;
        const sub = payable === q.extras.length ? 'Payable locally · not in package cost'
            : (payable === 0 ? 'Included in your package' : 'Add-ons for your trip');
        sectionHeader('Optional Extras', sub);
        q.extras.forEach((e) => {
            const detail = [e.detail, e.count > 1 ? `× ${e.count}` : '', e.included ? 'included in package price' : 'payable locally'].filter(Boolean).join(' · ');
            ensure(13);
            font('helvetica', 'normal', 9.5, C.dark);
            pdf.text(e.name, M + 3, y + 5);
            font('helvetica', 'normal', 7.2, C.mid);
            pdf.text(detail, M + 3, y + 9.4);
            if (e.included) {
                font('helvetica', 'bold', 8, C.green);
                pdf.text('Included', M + CW - 3, y + 7, { align: 'right' });
            } else {
                font('helvetica', 'bold', 9.5, C.dark);
                pdf.text(formatINR(e.price), M + CW - 3, y + 7, { align: 'right' });
            }
            y += 13;
            hair(M, y, M + CW);
        });
        y += 8;
    }

    // ── Important notes
    if (q.notes.length) {
        font('helvetica', 'normal', 8.3);
        const lines = q.notes.flatMap((n) => wrap(n, CW - 12));
        const boxH = 12 + lines.length * 4;
        ensure(boxH + 4);
        fill([255, 249, 230]);
        stroke([245, 215, 120], 0.3);
        pdf.roundedRect(M, y, CW, boxH, 2.5, 2.5, 'FD');
        font('helvetica', 'bold', 8.5, [133, 90, 10]);
        pdf.text('Important notes', M + 6, y + 6.5);
        font('helvetica', 'normal', 8.3, C.body);
        pdf.text(lines, M + 6, y + 11.5, { lineHeightFactor: 1.4 });
        y += boxH + 6;
    }

    // ── Documents & terms
    {
        const terms = [
            'This is a preliminary quotation and is subject to change based on availability.',
            'Rates are valid for the indicated travel dates only.',
            '25% advance is required to confirm the booking. Cancellation charges apply as per policy.',
        ];
        const label = 'Documents & terms: ';
        const leadText = 'ID requirements, permits, payment and cancellation terms will be shared with you by email. Please read them before confirming.';
        font('helvetica', 'bold', 7.8);
        const labelW = pdf.getTextWidth(label);
        font('helvetica', 'normal', 7.8);
        const firstLine = wrap(leadText, CW - 12 - labelW)[0];
        const restLines = wrap(leadText.slice(firstLine.length).trim(), CW - 12).filter(Boolean);
        font('helvetica', 'normal', 7.3);
        const termLines = terms.map((t) => wrap(t, CW - 16));
        const boxH = 10 + restLines.length * 3.8 + 2 + termLines.reduce((h, t) => h + t.length * 3.6 + 0.8, 0);
        ensure(boxH + 4);
        fill([244, 246, 245]);
        pdf.roundedRect(M, y, CW, boxH, 2.5, 2.5, 'F');
        let ty = y + 7;
        font('helvetica', 'bold', 7.8, C.dark);
        pdf.text(label, M + 6, ty);
        font('helvetica', 'normal', 7.8, C.body);
        pdf.text(firstLine, M + 6 + labelW, ty);
        restLines.forEach((line) => { ty += 3.8; pdf.text(line, M + 6, ty); });
        ty += 5.5;
        font('helvetica', 'normal', 7.3, C.mid);
        termLines.forEach((t) => {
            fill(C.mid);
            pdf.circle(M + 7, ty - 1, 0.45, 'F');
            pdf.text(t, M + 10, ty);
            ty += t.length * 3.6 + 0.8;
        });
        y += boxH + 8;
    }

    // ── Consultant + Google reviews / Instagram QR cards
    {
        const consultH = 36;
        const qrCardH = 36;
        const gap = 6;
        ensure(consultH + gap + qrCardH + 24);

        // "Prepared by" card — the consultant who created this quotation, signed
        const consultantName = clean(consultant.name) || 'EasoTrip Travel Desk';
        stroke(C.green, 0.4);
        pdf.setLineDashPattern([1.4, 1], 0);
        fill(C.greenTint);
        pdf.roundedRect(M, y, CW, consultH, 2.5, 2.5, 'FD');
        pdf.setLineDashPattern([], 0);
        kicker('Your Trip Consultant', M + 6, y + 7.5, C.green, 6);

        if (hasSignatureFont) font('GreatVibes', 'normal', 27, C.green);
        else font('times', 'bolditalic', 20, C.green);
        const sigText = wrap(consultantName, CW * 0.55)[0];
        pdf.text(sigText, M + 7, y + 19.5);
        const sigW = Math.max(pdf.getTextWidth(sigText), 45);
        stroke(C.mid, 0.25);
        pdf.line(M + 6, y + 22.5, M + 6 + sigW + 8, y + 22.5);

        font('helvetica', 'bold', 8.5, C.dark);
        pdf.text(consultantName, M + 6, y + 27.5);
        const nameW = pdf.getTextWidth(consultantName);
        font('helvetica', 'normal', 7.6, C.mid);
        pdf.text('  ·  Travel Consultant, EasoTrip', M + 6 + nameW, y + 27.5);
        font('helvetica', 'normal', 7.8, C.body);
        const contact = [clean(consultant.phone) || COMPANY.phone, clean(consultant.email) || COMPANY.email].join('   ·   ');
        pdf.text(contact, M + 6, y + 31.8);

        // Small "Prepared for you" stamp on the right
        font('helvetica', 'bold', 5.6);
        const prepW = pdf.getTextWidth('PREPARED FOR') + 0.9 * ('PREPARED FOR'.length - 1);
        kicker('Prepared for', M + CW - 6 - prepW, y + 13, C.mid, 5.6);
        font('times', 'bold', 12, C.dark);
        pdf.text(wrap(q.guestName, CW * 0.35)[0], M + CW - 6, y + 19.5, { align: 'right' });
        if (quotationNo) {
            font('helvetica', 'bold', 7.5, C.green);
            pdf.text(`Quote ID: ${quotationNo}`, M + CW - 6, y + 24.5, { align: 'right' });
        }
        y += consultH + gap;

        const cardW = (CW - gap) / 2;
        const qrCard = (x, { title, line, cta, url, qrImage, icon }) => {
            stroke(C.rule, 0.3);
            fill(C.white);
            pdf.roundedRect(x, y, cardW, qrCardH, 2.5, 2.5, 'FD');
            const qrS = qrCardH - 8;
            if (qrImage) pdf.addImage(qrImage, 'PNG', x + 4, y + 4, qrS, qrS);
            const tx = x + 4 + (qrImage ? qrS + 5 : 2);
            const tw = cardW - (tx - x) - 4;
            font('times', 'bold', 12, C.dark);
            const titleLines = wrap(title, tw);
            pdf.text(titleLines, tx, y + 9.5, { lineHeightFactor: 1.15 });
            let ly = y + 9.5 + (titleLines.length - 1) * 4.9 + 5.8;
            icon(tx, ly);
            ly += 7.2;
            font('helvetica', 'bold', 7.8, C.green);
            const ctaLines = wrap(cta, tw - 4);
            pdf.text(ctaLines, tx, ly, { lineHeightFactor: 1.3 });
            const lastW = pdf.getTextWidth(ctaLines[ctaLines.length - 1]);
            arrowUpRight(tx + lastW + 1.8, ly + (ctaLines.length - 1) * 3.6 + 0.1, 1.8);
            if (line) {
                font('helvetica', 'normal', 7, C.mid);
                pdf.text(line, tx, y + qrCardH - 4);
            }
            pdf.link(x, y, cardW, qrCardH, { url });
        };

        qrCard(M, {
            title: 'See what our travellers say',
            cta: 'Scan or tap to read our Google reviews',
            url: GOOGLE_REVIEWS_URL,
            qrImage: qr.review,
            icon: (x, yy) => { for (let i = 0; i < 5; i++) star(x + 2 + i * 4.8, yy - 1.2, 1.85); },
        });
        qrCard(M + cardW + gap, {
            title: 'Follow our journeys',
            cta: 'Scan or tap to follow us on Instagram',
            url: INSTAGRAM_URL,
            qrImage: qr.instagram,
            icon: (x, yy) => {
                instagramIcon(x, yy - 3.4, 3.6, C.green);
                font('helvetica', 'bold', 9, C.dark);
                pdf.text(COMPANY.instagram, x + 5.4, yy - 0.5);
            },
        });
        y += qrCardH + 16;

        font('times', 'italic', 16, C.dark);
        pdf.text('Book · Relax · Travel · Repeat', W / 2, y, { align: 'center' });
    }

    // ── Running footer on every page after the cover
    const total = pdf.internal.getNumberOfPages();
    for (let p = 2; p <= total; p++) {
        pdf.setPage(p);
        hair(M, H - 13, W - M);
        font('helvetica', 'normal', 7, C.mid);
        const footerText = `${COMPANY.web}  ·  ${COMPANY.phone}  ·  ${COMPANY.email}  ·  `;
        pdf.text(footerText, M, H - 8);
        const igX = M + pdf.getTextWidth(footerText);
        instagramIcon(igX, H - 10.3, 2.6, C.mid);
        pdf.text(COMPANY.instagram, igX + 3.6, H - 8);
        pdf.link(igX, H - 11, 3.6 + pdf.getTextWidth(COMPANY.instagram), 4, { url: INSTAGRAM_URL });
        pdf.text(`Page ${p} of ${total}`, W - M, H - 8, { align: 'right' });
    }

    return pdf;
};

// ── Asset loading (browser) ─────────────────────────────────────────────────

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
});

// Center-crop to the target aspect ratio and recompress so the PDF stays small
const cropToAspect = (src, aspect, maxW = 1400) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        try {
            let sw = img.naturalWidth;
            let sh = img.naturalHeight;
            if (sw / sh > aspect) sw = sh * aspect; else sh = sw / aspect;
            const sx = (img.naturalWidth - sw) / 2;
            const sy = (img.naturalHeight - sh) / 2;
            const outW = Math.min(maxW, Math.round(sw));
            const canvas = document.createElement('canvas');
            canvas.width = outW;
            canvas.height = Math.round(outW / aspect);
            canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.82));
        } catch (_) {
            resolve(null);
        }
    };
    img.onerror = () => resolve(null);
    img.src = src;
});

const loadPhoto = async (url, aspect, maxW) => {
    if (!url) return null;
    let src = null;
    try {
        const { proxyImage } = await import('../../api/imageUploadApi');
        src = (await proxyImage(url))?.dataUrl || null;
    } catch (_) { /* fall back to a direct (CORS) fetch */ }
    if (!src) {
        try {
            const res = await fetch(url, { mode: 'cors' });
            if (res.ok) src = await blobToDataUrl(await res.blob());
        } catch (_) { /* no photo for this slot */ }
    }
    return src ? cropToAspect(src, aspect, maxW) : null;
};

const loadPlaces = async () => {
    try {
        const { getAllplaces } = await import('../../api/placeApi');
        const res = await getAllplaces();
        return Array.isArray(res) ? res : (res?.data || []);
    } catch (_) {
        return [];
    }
};

const loadPublicImage = async (path) => {
    try {
        const res = await fetch(path);
        return res.ok ? await blobToDataUrl(await res.blob()) : null;
    } catch (_) {
        return null;
    }
};

const loadSignatureFont = async () => {
    try {
        const res = await fetch(SIGNATURE_FONT_URL);
        if (!res.ok) return null;
        const bytes = new Uint8Array(await res.arrayBuffer());
        let binary = '';
        for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
        return btoa(binary);
    } catch (_) {
        return null;
    }
};

// List screens don't always carry quotation_no / created_by — fetch the saved query when missing
const resolveInquiry = async (inquiry) => {
    const qNo = inquiry?.quotation_no || inquiry?.quotationNo || inquiry?.quote_id || inquiry?.quoteNo;
    if (!inquiry?._id || (qNo && inquiry.created_by)) return inquiry;
    try {
        const { getSingleQuery } = await import('../../api/queriesAPI');
        const res = await getSingleQuery(inquiry._id);
        const saved = res?.data || res;
        return saved?._id ? {
            ...inquiry,
            quotation_no: qNo || saved.quotation_no || saved.quotationNo || saved.quote_id || saved.quoteNo,
            created_by: inquiry.created_by || saved.created_by,
        } : inquiry;
    } catch (_) {
        return inquiry; // not a saved query (e.g. a raw enquiry)
    }
};

const userToConsultant = (u) => (u && typeof u === 'object' && (u.fullName || u.name)
    ? { name: u.fullName || u.name, email: u.email, phone: u.phone }
    : null);

/**
 * The consultant shown on the PDF is whoever created the quotation.
 * Saved quotations: creator from the query (populated object, aggregate name, or looked up by id).
 * New quotations: the logged-in user, who will be saved as the creator.
 */
const resolveConsultant = async ({ inquiry, currentUser }) => {
    if (!inquiry || !inquiry._id) return userToConsultant(currentUser) || {};

    const creator = Array.isArray(inquiry.creator) ? inquiry.creator[0] : inquiry.creator;
    let found = userToConsultant(creator) || userToConsultant(inquiry.created_by)
        || (inquiry.created_by_name ? { name: inquiry.created_by_name } : null);

    const creatorId = typeof inquiry.created_by === 'string' ? inquiry.created_by : inquiry.created_by?._id;
    if ((!found || !found.email) && creatorId) {
        try {
            const { getSingleUser } = await import('../../api/userAPI');
            const res = await getSingleUser(creatorId);
            const u = Array.isArray(res?.data) ? res.data[0] : res?.data;
            found = userToConsultant(u) || found;
        } catch (_) { /* keep what we have */ }
    }
    return found || userToConsultant(currentUser) || {};
};

/**
 * Generates and downloads the premium A4 quotation PDF.
 * `data` is the CreateInquiry state; `data.quotationNo` (the query's quotation_no) is printed on the cover.
 */
export const exportQuotationPDF = async (data, setLoading, showSnackbar) => {
    setLoading(true);
    try {
        const shaped = shapeQuotationData(data);
        const inquiry = await resolveInquiry(data.inquiry);
        const quotationNo = clean(
            data.quotationNo ||
            data.quotation_no ||
            data.quote_id ||
            data.quoteNo ||
            inquiry?.quotation_no ||
            inquiry?.quotationNo ||
            inquiry?.quote_id ||
            inquiry?.quoteNo
        ) || null;
        const [logo, qrReview, qrInstagram, places, signatureFont, consultant] = await Promise.all([
            loadPublicImage('/easo.png'),
            loadPublicImage(QR_IMAGES.review),
            loadPublicImage(QR_IMAGES.instagram),
            loadPlaces(),
            loadSignatureFont(),
            resolveConsultant({ inquiry, currentUser: data.currentUser }),
        ]);
        const plan = planQuotationPhotos({ places, tripTitle: shaped.tripTitle, days: shaped.days });

        const [coverData, ...dayData] = await Promise.all([
            plan.cover ? loadPhoto(plan.cover.url, COVER_PHOTO.w / COVER_PHOTO.h, 1600) : null,
            ...plan.days.map((p) => (p ? loadPhoto(p.url, DAY_PHOTO.w / DAY_PHOTO.h, 900) : null)),
        ]);

        const pdf = await buildQuotationPdf(data, {
            logo,
            cover: coverData ? { data: coverData, caption: plan.cover.caption } : null,
            dayPhotos: plan.days.map((p, i) => (p && dayData[i] ? { data: dayData[i], caption: p.caption } : null)),
            consultant,
            signatureFont,
            qr: { review: qrReview, instagram: qrInstagram },
            quotationNo,
        });

        pdf.save(generatePdfFilename(data.guestInfo?.guest_name, data.tripDetails?.location));
        showSnackbar('PDF exported successfully!', 'success');
    } catch (error) {
        console.error('Error exporting PDF:', error);
        showSnackbar('Failed to export PDF', 'error');
    } finally {
        setLoading(false);
    }
};
